'use client';

import { useBunja } from 'bunja/react';
import { atom, useSetAtom } from 'jotai';
import { useRouter } from 'next/navigation';
import { useCallback } from 'react';

import { postAuthBySocialApi } from '@flipster/apis/v1/social';
import { AuthBySocialErrorResponse } from '@flipster/apis/v1/social/types';
import { postUserAuthMfaConfirmApi } from '@flipster/apis/v1/user';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { supportedLocaleToLanguageTag } from '@flipster/i18n/constants';
import { useSupportedLocale } from '@flipster/i18n/hooks';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { alert } from '@flipster/ui/Alert';
import { checkAxiosError } from '@flipster/utils/axios';
import { getErrorCode, getErrorMessage } from '@flipster/utils/error';

import { ninetyDaysPasswordUpdateBunja } from '@web/app/[locale]/(web)/(session)/login/_90days-password-update';
import { mfaVerifyFlowBunja } from '@web/app/[locale]/(web)/_mfa/bunja';
import { varaTransitionAgreementBunja } from '@web/app/[locale]/(web)/_modal/VaraTransitionAgreementModal/bunja';

import { useBlockedRegion } from './useBlockedRegion';

export type SocialProviderType = 'GOOGLE' | 'APPLE';

export const socialTokenAtom = atom<string | undefined>(undefined);
export const socialProviderAtom = atom<SocialProviderType | undefined>(undefined);

export const useSocialLogin = () => {
  const setSocialToken = useSetAtom(socialTokenAtom);
  const setSocialProvider = useSetAtom(socialProviderAtom);
  const { openPasswordChangeRequiredModal } = useBunja(ninetyDaysPasswordUpdateBunja);
  const { isRestrictedRegion, regionAlert } = useBlockedRegion();
  const { startVerify } = useBunja(mfaVerifyFlowBunja);
  const locale = useSupportedLocale();
  const router = useRouter();
  const { getMe } = useAuth();
  const { checkTncAgreementModalNeedToShow, openTncAgreementModal } = useBunja(varaTransitionAgreementBunja);
  return useCallback(
    async (provider: SocialProviderType, credential: string) => {
      try {
        const { data } = await postAuthBySocialApi({
          provider,
          credential,
          lang: supportedLocaleToLanguageTag[locale],
        });

        const { authToken, requireMfa, phoneNumber } = data ?? {};

        const checkLogin = async () => {
          const { data: me } = await getMe();
          if (me) {
            openPasswordChangeRequiredModal(me);
            const isTncAgreementModalNeedToShow = await checkTncAgreementModalNeedToShow(me.userId);

            if (isTncAgreementModalNeedToShow) openTncAgreementModal({ from: 'organic_login' });
          }
        };
        if (authToken && requireMfa) {
          startVerify('AUTH', {
            authToken,
            phoneNumber,
            onConfirm: async (mfaSessionToken) => {
              await postUserAuthMfaConfirmApi({ mfaSessionToken, authToken });
              await checkLogin();
            },
          });
        } else {
          await checkLogin();
        }
      } catch (err) {
        if (!checkAxiosError(err)) return;
        const errorCode = getErrorCode(err);
        const { token } = err.response!.data as AuthBySocialErrorResponse;
        if (errorCode === 'api.unauthorized' && token) {
          // TODO 이거 회원가입 하려고 했다가 뒤로 돌아가서 재시도시 동작이 안됨!!

          setSocialToken(token);
          setSocialProvider(provider);

          if (isRestrictedRegion) {
            regionAlert('/social-signup');
          } else {
            router.push('/social-signup');
          }
          return;
        }
        if (errorCode === 'api.user.auth.noEmailInSocialCredential') {
          alert.show({
            title: 'Social provider error',
            message: getErrorMessage(err),
          });
          return;
        }
        snackbar.error({ message: getErrorMessage(err) });
      }
    },
    [
      getMe,
      openPasswordChangeRequiredModal,
      router,
      setSocialProvider,
      setSocialToken,
      locale,
      startVerify,
      checkTncAgreementModalNeedToShow,
      isRestrictedRegion,
      openTncAgreementModal,
      regionAlert,
    ],
  );
};
